import React from "react";
import { useHotkeys } from "react-hotkeys-hook";

import AppConstants from "../constants/AppConstants";
import { useApiConfig } from "../hooks/query/useApiConfig";
import Stage from "./Stage";
import SidePanel from "./SidePanel";
import DesignViewContainer from "./DesignViewContainer";
import StageContainer from "./StageContainer";
import DesignSettings from "./DesignSettings";
import PopupRenameDesignItem from "./PopupRenameDesignItem";
import DesignViewModals, { MODAL_LIST } from "./DesignViewModals";
import SitePanel from "./sitepanel/SitePanel/SitePanel";
import PropertiesPanel from "../app2components/panel/PropertiesPanel";
import DesignViewStore from "../stores/DesignViewStore";
import ImageUploadStore from "../stores/ImageUploadStore";
import ActiveContainer from "../stores/ActiveBlockContainerStore.mjs";
import UIActions from "../actions/UIActions";
import SiteActions from "../actions/SiteActions";
import TransformActions from "../actions/TransformActions";
import DocumentStore from "../document/DocumentStore";
import { COMMANDS } from "../document/commands";
import WorkInProgressOverlay from "./WorkInProgressOverlay";
import MediaLoadingOverlay from "./MediaLoadingOverlay";
import JSONEditor from "./JSONEditor";
import ModalController from "./utils/ModalController";
import Toolbar from "../app2components/toolbar/Toolbar";
import useStoreState from "./providers/useStoreState";
import {
  CROP_MODE,
  DEFAULT_MODE,
  GROUP_FOCUS_MODE,
  ZOOM_INCREMENT,
} from "../constants/UIConstants";
import CommandPalette from "../app2components/toolbar/CommandPalette";
import Footer from "../app2components/footer/Footer";
import DragOverlay from "./base/DragOverlay";
import {
  LAYER_BOUND_BACK,
  LAYER_BOUND_FRONT,
  DIRECTION_UP,
  DIRECTION_DOWN,
} from "../constants/LayerConstants";
import { moveElementLayers, moveElementsToBound } from "../utils/LayerUtils";
import noOp from "@/utils/noOp";
import ImageActions from "@/actions/ImageActions";
import GroupActions from "@/actions/GroupActions";
import GroupStore from "@/stores/GroupStore";
import DataTypes from "@/constants/DataTypes";
import {
  canSelectionGroup,
  canSelectedGroupDissolve,
  groupSelection,
  dissolveSelection,
} from "@/utils/GroupUtils";

const { PANEL_THEME } = AppConstants;

const getSelectedElements = (blockContainer) => {
  const block = blockContainer.get("selectedBlock");
  const isPartOfCanvasSet = block.get("canvasSet");

  if (!block.isEmpty() && !isPartOfCanvasSet) {
    return block.getIn(["selected", "elements"]);
  }
};

const _handleToggleSnapping = () => {
  UIActions.toggleStageOverlay("enableSnapping");
};

const _handleCopy = () => {
  const selection = window.getSelection();
  if (selection && selection.type === "Range") {
    // Allow system copy action to occur
  } else {
    SiteActions.copy();
    return false;
  }
};

const _handleSpecialPaste = (blockContainer) => {
  DocumentStore.dispatch({ type: COMMANDS.SPECIAL_PASTE, payload: blockContainer.get("theme") });
};

const _handlePaste = () => {
  DocumentStore.dispatch({ type: COMMANDS.PASTE });
};

const _handleUndo = () => {
  DocumentStore.dispatch({ type: COMMANDS.UNDO });
};

const _handleDuplicate = () => {
  SiteActions.copy({});
  // Necessary to wait for the copy to complete before pasting
  setTimeout(() => DocumentStore.dispatch({ type: COMMANDS.PASTE, payload: {} }), 100);
};

const _handleCut = () => {
  const selection = window.getSelection();
  if (!selection || selection.type !== "Range") {
    SiteActions.copy({ cut: true });
  }
};

const _handleRedo = () => {
  DocumentStore.dispatch({ type: COMMANDS.REDO });
};

const _handleEditJSON = () => {
  UIActions.toggleJSONEditor();
};

const _handleArrows = (e, { keys, shift }) => {
  const amount = (shift ? 10 : 1) * (keys.includes("up") || keys.includes("left") ? -1 : 1);
  const direction = keys.includes("up") || keys.includes("down") ? "y" : "x";
  TransformActions.shift(direction, amount);
};

const _hideThemeModal = () => {
  UIActions.toggleActiveTheme({ activeTheme: null });
};

const _hideJSONEditor = () => {
  UIActions.toggleJSONEditor();
};

const _hideRenamePopup = () => {
  UIActions.toggleRenamePopup(null);
};

const _groupSelection = (blockContainer) => {
  const selectedElements = getSelectedElements(blockContainer);
  if (canSelectionGroup(selectedElements)) {
    const block = blockContainer.get("selectedBlock");
    groupSelection(selectedElements, block);
  }
};

const _ungroupSelection = (blockContainer) => {
  const selectedElements = getSelectedElements(blockContainer);
  if (canSelectedGroupDissolve(selectedElements)) {
    const block = blockContainer.get("selectedBlock");
    dissolveSelection(selectedElements, block);
  }
};

/**
 *
 * @param {typeof CROP_MODE | undefined} mode
 * @returns
 */
const cancelHandler = (mode, layout) =>
  ({
    [CROP_MODE]: ImageActions.exitCropMode,
    [GROUP_FOCUS_MODE]: () => {
      const selectionId = GroupStore.getGroupSelectionId();
      const blockId = GroupStore.getBlockId();

      UIActions.selectElement({
        id: selectionId,
        type: DataTypes.GROUP,
        layout,
        blockId: blockId,
      });

      GroupActions.blurGroup();
    },
  })[mode] ?? noOp;

const preventDefault = true;

const DesignView = () => {
  useApiConfig();
  const blockContainer = useStoreState(ActiveContainer, ActiveContainer.getContainer);
  const view = useStoreState(DesignViewStore, DesignViewStore.getState);
  const imageUploadState = useStoreState(ImageUploadStore, ImageUploadStore.getState);
  const mode = view.getIn(["mode"]);
  const modeType = mode.get("type");
  const canEdit = mode.get("allowEdits", true);
  const ignoreEventWhen = () => !canEdit;

  const layout = blockContainer.get("layout");

  const _zoomIn = () => {
    UIActions.incrementZoom(ZOOM_INCREMENT, layout);
  };

  const _zoomOut = () => {
    UIActions.incrementZoom(-ZOOM_INCREMENT, layout);
  };

  const _zoomReset = () => {
    UIActions.zoomReset(layout);
  };

  const _zoomToWidth = () => {
    UIActions.zoomToWidth(layout, blockContainer);
  };

  const _handleDelete = () => {
    if (DesignViewStore.isStageActive()) {
      DocumentStore.dispatch({ type: COMMANDS.REMOVE_SELECTED_ELEMENTS });
    }
  };

  const _handleLock = () => {
    DocumentStore.dispatch({ type: COMMANDS.TOGGLE_ITEMS_LOCK_STATUS });
  };

  useHotkeys("delete, backspace", _handleDelete, {
    preventDefault,
    ignoreEventWhen,
  });
  useHotkeys("mod+z", _handleUndo, {
    preventDefault,
    ignoreEventWhen,
  });
  useHotkeys("mod+c", _handleCopy, {
    preventDefault,
    ignoreEventWhen,
  });
  useHotkeys("mod+x", _handleCut, {
    preventDefault,
    ignoreEventWhen,
  });
  useHotkeys("mod+v", _handlePaste, {
    preventDefault,
    ignoreEventWhen,
  });
  useHotkeys("mod+d", _handleDuplicate, {
    preventDefault,
    ignoreEventWhen,
  });
  useHotkeys("mod+;", _handleToggleSnapping);
  useHotkeys("shift+mod+v", () => _handleSpecialPaste(blockContainer), {
    ignoreEventWhen,
  });
  useHotkeys(["shift+mod+z", "mod+y"], _handleRedo, {
    ignoreEventWhen,
  });
  useHotkeys(
    ["shift+up", "up", "shift+down", "down", "shift+left", "left", "shift+right", "right"],
    _handleArrows,
    { preventDefault, ignoreEventWhen },
  );
  // disables arrow scroll when in crop mode. Will be replaced with shift handler eventually.
  useHotkeys(
    ["shift+up", "up", "shift+down", "down", "shift+left", "left", "shift+right", "right"],
    () => {},
    { preventDefault, ignoreEventWhen: () => modeType !== CROP_MODE },
  );
  useHotkeys("mod+l", _handleLock, { preventDefault, ignoreEventWhen });
  useHotkeys("mod+bracketleft", () => moveElementLayers(DIRECTION_DOWN), {
    preventDefault,
    ignoreEventWhen,
  });
  useHotkeys("mod+bracketright", () => moveElementLayers(DIRECTION_UP), {
    preventDefault,
    ignoreEventWhen,
  });
  useHotkeys("mod+alt+bracketleft", () => moveElementsToBound(LAYER_BOUND_BACK), {
    preventDefault,
    ignoreEventWhen,
  });
  useHotkeys("mod+alt+bracketright", () => moveElementsToBound(LAYER_BOUND_FRONT), {
    preventDefault,
    ignoreEventWhen,
  });

  useHotkeys("esc", cancelHandler(modeType, layout));
  useHotkeys("shift+alt+mod+e", _handleEditJSON);
  useHotkeys("mod+equal", _zoomIn, { preventDefault });
  useHotkeys("mod+minus", _zoomOut, { preventDefault });
  useHotkeys("mod+0", _zoomReset, { preventDefault });
  useHotkeys("mod+alt+0", _zoomToWidth, { preventDefault });
  useHotkeys("enter", () => ImageActions.exitAndSaveCropMode(), {
    preventDefault,
    ignoreEventWhen: () => modeType !== CROP_MODE,
  });
  useHotkeys("shift+mod+g", () => _ungroupSelection(blockContainer), {
    ignoreEventWhen,
    preventDefault,
  });
  useHotkeys("mod+g", () => _groupSelection(blockContainer), {
    ignoreEventWhen,
    preventDefault,
  });

  const _loadModals = () =>
    MODAL_LIST.map((modalName) => {
      const modalData = view.getIn(["modals", modalName], false);
      return ModalController.getModal(this, modalName, modalData, DesignViewModals[modalName]);
    });

  const style = {
    height: "100%",
  };

  const leftPanel = view.get("leftPanel");
  const rightPanel = view.get("rightPanel");
  const stage = view.get("stage");

  const sitePanel = view.get("sitePanel");
  const propertiesPanel = view.get("propertiesPanel");
  const stageView = stage.get("view");

  const viewModals = _loadModals();

  const viewTheme = view.get("activeTheme");
  let showThemeModal = false;
  if (viewTheme === PANEL_THEME) {
    showThemeModal = true;
  }

  let showRenamePopup = false;
  const renamePopupConfig = view.get("renamePopup");
  if (renamePopupConfig) {
    showRenamePopup = true;
  }

  const showJSONEditor = view.get("jsonEditorActive");
  let modal = null;
  if (showJSONEditor) {
    modal = <JSONEditor show={showJSONEditor} onHide={_hideJSONEditor} container={this} />;
  }

  let workingOverlay = null;
  if (view.get("working", false)) {
    const message = view.get("workingMessage");
    workingOverlay = <WorkInProgressOverlay message={message} />;
  }

  let imageUploadOverlay = null;
  const imageUploadOverlayState = imageUploadState.get("imageUploadOverlay");
  if (imageUploadOverlayState && imageUploadOverlayState.get("show", false)) {
    const message = imageUploadOverlayState.get("message");
    imageUploadOverlay = <MediaLoadingOverlay show message={message} />;
  }

  return (
    <div style={style}>
      <Toolbar />
      <DesignViewContainer>
        <StageContainer key="stageContainer">
          <SidePanel
            key="leftPanel"
            position="left"
            toggle={UIActions.showLeftPanelView}
            open={leftPanel.get("open")}
          >
            <SitePanel
              key="sitePanel"
              stageView={stageView}
              viewState={sitePanel}
              blockContainer={blockContainer}
            />
          </SidePanel>
          <Stage key="stage" stage={stage} viewState={view} blockContainer={blockContainer} />
          <SidePanel
            id="propertiesPanel"
            key="rightPanel"
            position="right"
            toggle={UIActions.showRightPanelView}
            open={rightPanel.get("open")}
          >
            <PropertiesPanel
              key="propertiesPanel"
              stageView={stageView}
              viewState={propertiesPanel}
              blockContainer={blockContainer}
            />
          </SidePanel>
        </StageContainer>
      </DesignViewContainer>
      <Footer view={view} blockContainer={blockContainer} />
      <DesignSettings
        show={showThemeModal}
        onHide={_hideThemeModal}
        blockContainer={blockContainer}
        modalTitle="Design Settings"
        modalActionButton="Save"
      />
      {showRenamePopup ? (
        <PopupRenameDesignItem
          show={showRenamePopup}
          onHide={_hideRenamePopup}
          {...renamePopupConfig}
        />
      ) : null}
      <DragOverlay />
      {modeType === DEFAULT_MODE && <CommandPalette />}
      {viewModals}
      {modal}
      {workingOverlay}
      {imageUploadOverlay}
    </div>
  );
};

export default DesignView;
